import {
  AlertTriangle,
  Ban,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  Compass,
  Contact,
  FileSignature,
  FileText,
  Gauge,
  History,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Leaf,
  type LucideIcon,
  MailCheck,
  Map,
  MapPin,
  NotebookPen,
  Scale,
  SendHorizontal,
  Settings2,
  ShieldCheck,
  ShieldUser,
  Ship,
  ShoppingCart,
  Star,
  ToggleRight,
  Users,
  Waypoints,
  Workflow,
} from 'lucide-react';

import { paths } from '$routes/paths';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  description?: string;
  /** Match the path exactly rather than as a prefix. */
  end?: boolean;
  /** Not built yet — routes to the placeholder page. */
  soon?: boolean;
}

export interface NavSection {
  heading?: string;
  icon: LucideIcon;
  items: NavItem[];
}

/**
 * Central navigation model for the admin shell. Add new sections/items here and
 * they appear in the sidebar automatically — the shell handles grouping,
 * collapsing and active-state highlighting.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Overview',
    icon: Compass,
    items: [
      {
        to: paths.dashboard.root,
        label: 'Dashboard',
        icon: LayoutDashboard,
        end: true,
        description: 'Admin overview',
      },
      {
        to: `${paths.dashboard.root}/stats`,
        label: 'Statistics',
        icon: Gauge,
        end: true,
        description: 'KPIs and login statistics',
      },
      {
        to: paths.dashboard.biReports.root,
        label: 'BI Reports',
        icon: BarChart3,
        description: 'Power BI dashboards',
      },
    ],
  },
  {
    heading: 'Ops',
    icon: Waypoints,
    items: [
      {
        to: paths.dashboard.shipments.root,
        label: 'Shipments',
        icon: Ship,
        description: 'Track all shipments',
      },
      {
        to: paths.dashboard.fleet.root,
        label: 'Fleet Map',
        icon: Map,
        description: 'Live vessel positions',
      },
      {
        to: paths.dashboard.bookings.root,
        label: 'Bookings',
        icon: CalendarCheck,
        description: 'All bookings',
      },
      {
        to: paths.dashboard.emissionReports.all,
        label: 'Emission Reports',
        icon: Leaf,
        description: 'Carbon reporting',
      },
      {
        to: paths.dashboard.nonConformities.root,
        label: 'Non-conformities',
        icon: AlertTriangle,
        description: 'Issues and complaints',
      },
    ],
  },
  {
    heading: 'Sales',
    icon: BriefcaseBusiness,
    items: [
      {
        to: paths.dashboard.orders.root,
        label: 'Orders',
        icon: ShoppingCart,
        description: 'Order management',
      },
      {
        to: paths.dashboard.quotations.root,
        label: 'Quotations',
        icon: FileSignature,
        description: 'Issued quotations',
      },
      {
        to: paths.dashboard.quoteRequests.root,
        label: 'Quote Requests',
        icon: NotebookPen,
        description: 'Inbound quote requests',
      },
      {
        to: paths.dashboard.contracts.root,
        label: 'Contracts',
        icon: FileText,
        description: 'Customer contracts',
      },
    ],
  },
  {
    heading: 'Users & Clients',
    icon: ShieldUser,
    items: [
      {
        to: paths.dashboard.users.root,
        label: 'Users',
        icon: Users,
        description: 'Application users',
      },
      {
        to: paths.dashboard.clients.root,
        label: 'Clients',
        icon: Building2,
        description: 'Customer accounts',
      },
      {
        to: paths.dashboard.clientsAPI.root,
        label: 'Client API',
        icon: KeyRound,
        description: 'Client API subscriptions',
      },
      {
        to: paths.dashboard.locations.root,
        label: 'Locations',
        icon: MapPin,
        description: 'Master location data',
      },
      {
        to: paths.dashboard.addresses.root,
        label: 'Addresses',
        icon: Contact,
        description: 'Address book search',
      },
    ],
  },
  {
    heading: 'Administration',
    icon: ShieldCheck,
    items: [
      {
        to: paths.dashboard.releaseNotes.root,
        label: 'Release Notes',
        icon: NotebookPen,
        description: 'Product release notes',
      },
      {
        to: paths.dashboard.maintenance.root,
        label: 'Maintenance & Alerts',
        icon: Bell,
        description: 'System banners',
      },
      {
        to: paths.dashboard.features.root,
        label: 'Features',
        icon: ToggleRight,
        description: 'Feature flags',
      },
      {
        to: paths.dashboard.termsAndConditions.root,
        label: 'Terms & Conditions',
        icon: Scale,
        description: 'Legal documents',
      },
      {
        to: paths.dashboard.ratings.root,
        label: 'Ratings',
        icon: Star,
        description: 'Customer ratings',
      },
      {
        to: paths.dashboard.events.root,
        label: 'Events',
        icon: Bell,
        description: 'System event log',
      },
    ],
  },
  // {
  //   heading: 'Integrations & AI',
  //   icon: PlugZap,
  //   items: [
  //     {
  //       to: paths.dashboard.aiExperiments.mailClassification.root,
  //       label: 'Mail Classification',
  //       icon: Bot,
  //       description: 'AI email classification',
  //     },
  //   ],
  // },
  {
    heading: 'Mail Automation',
    icon: SendHorizontal,
    items: [
      {
        to: paths.dashboard.mailAutomation.root,
        label: 'Overview',
        icon: Gauge,
        description: 'Mail Automation operational flow',
        end: true,
      },
      {
        to: paths.dashboard.mailAutomation.scrapedEmails.root,
        label: 'Scraped Emails',
        icon: Inbox,
        description: 'Incoming scraped emails',
      },
      {
        to: paths.dashboard.mailAutomation.processedEmails.root,
        label: 'Processed Emails',
        icon: MailCheck,
        description: 'AI-processed emails and KPIs',
      },
      {
        to: paths.dashboard.mailAutomation.workflows.root,
        label: 'Workflows',
        icon: Workflow,
        description: 'Email processing workflows',
      },
      {
        to: paths.dashboard.mailAutomation.workflowRuns.root,
        label: 'Workflow Runs',
        icon: History,
        description: 'Workflow run history',
      },
      {
        to: paths.dashboard.mailAutomation.imageFingerprints.root,
        label: 'Ignored Images',
        icon: Ban,
        description: 'Review repeated and signature-like images',
      },
      {
        to: paths.dashboard.mailAutomation.settings.root,
        label: 'Settings',
        icon: Settings2,
        description: 'Environment-specific mail automation settings',
      },
    ],
  },
];

/** Flat list of every nav item, in order. */
export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

/**
 * Returns the `to` of the nav item that best matches the current path (the
 * longest matching prefix wins, so `/workflows/routes` beats `/workflows`).
 */
export function activeNavTo(pathname: string): string | null {
  let best: string | null = null;
  let bestLen = -1;
  for (const item of NAV_ITEMS) {
    const matches = item.end
      ? pathname === item.to
      : pathname === item.to || pathname.startsWith(item.to + '/');
    if (matches && item.to.length > bestLen) {
      best = item.to;
      bestLen = item.to.length;
    }
  }
  return best;
}
